import { ComponentChildren, h, JSX, RefCallback } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { classJoin } from "../helpers/classjoin"
import {
    getHandleCenterPosition,
    getMousePosition,
    getTouchPosition,
    isEventFromHandle,
    isNotCorrectTouchEvent,
    noop
} from "./utils"
import { Marks } from "./Marks"
import { Steps } from "./Steps"

/**
 * @group Components
 */
export interface AbstractSliderProps {
    /** Minimum value */
    min?: number
    /** Maximum value */
    max?: number
    /** Value to be added or subtracted on each step the slider makes */
    step?: number
    /** Marks on the slider */
    marks?: SliderMarks
    /** Show dots on slider (with step as interval)? */
    dots?: boolean
    /** As continuous value interval (otherwise, as independent values)? */
    included?: boolean
    /** Vertical mode? */
    vertical?: boolean
    /** Control disabled? */
    disabled?: boolean
    /** Component main class name */
    className?: string
    /** Prefix for secondary class names in component */
    classesPrefix?: string
    /** Triggered before value is start to change (on mouse down, etc) */
    onBeforeChange?(value: SliderValue): void
    /** Triggered while the value of Slider changing */
    onChange?(value: SliderValue): void
    /** Triggered after slider changes stop (on mouse up, etc) */
    onAfterChange?(value: SliderValue): void
    /** A function to format value on tooltip */
    tipFormatter?(value: number): ComponentChildren
    tracks?: JSX.Element[]
    handles?: JSX.Element[]
    bounds?: number[]
    upperBound?: number
    lowerBound?: number
    onStart?: (position: number) => void
    onEnd?: () => void
    onMove?: (position: number) => void
    sliderRefCallback?: RefCallback<HTMLDivElement> | undefined
    handlesRefs?: Element[]
    activeHandle?: number | null
    children?: ComponentChildren
}

/**
 * Marks on the slider. The key determines the position, and the value
 * determines what will show.
 */
export interface SliderMarks {
    [key: number]: ComponentChildren
}

/**
 * Allowed type of slider value.
 */
export type SliderValue = number | number[]

/**
 * Buttons in MouseEvent.button.
 */
const MouseButtons = {
    LEFT: 0,
    MODDLE: 1,
    RIGHT: 2,
    BACK: 3,
    FORWARD: 4
} as const

/**
 * Abstract slider class (for single and multi slider).
 *
 * @group Components
 */
export function AbstractSlider({
    min = 0,
    max = 100,
    step = 1,
    marks = {},
    dots = false,
    included = true,
    vertical = false,
    disabled = false,
    className = "range-slider",
    classesPrefix = "",
    children,
    tracks = [],
    handles = [],
    bounds = [],
    lowerBound = 0,
    upperBound = 0,
    sliderRefCallback,
    activeHandle = null,
    handlesRefs = [],
    onStart = noop,
    onEnd = noop,
    onMove = noop,
    onAfterChange = noop
}: AbstractSliderProps) {
    /**
     * Current cursor offset while dragging.
     */
    const dragOffset = useRef(0)
    const sliderRef = useRef<HTMLDivElement | null>(null)

    /**
     * Pass ref to the parent component.
     */
    useEffect(() => {
        if (sliderRefCallback) {
            sliderRefCallback(sliderRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Effect is necessary on every render to get latest props in event listener methods.
     */
    useEffect(() => {
        removeDocumentEvents()

        if (activeHandle === null) {
            return
        }

        addDocumentMouseEvents()
        addDocumentTouchEvents()

        return () => {
            removeDocumentEvents()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    })

    const classes = classJoin(
        {
            [`with-marks`]: !!Object.keys(marks).length,
            vertical,
            disabled,
            [className]: true
        },
        cl => classesPrefix + cl
    )

    /**
     * Start mouse event.
     */
    function onMouseDown(event: MouseEvent): void {
        if (event.button !== MouseButtons.LEFT) {
            return
        }

        let position: number = getMousePosition(vertical, event)

        if (isEventFromHandle(event, handlesRefs) && event.target instanceof Element) {
            const handlePosition = getHandleCenterPosition(vertical, event.target)
            dragOffset.current = position - handlePosition
            position = handlePosition
        } else {
            dragOffset.current = 0
        }

        onStart(position)
    }

    /**
     * Start touch event.
     */
    function onTouchStart(event: TouchEvent): void {
        if (isNotCorrectTouchEvent(event)) {
            return
        }

        let position: number = getTouchPosition(vertical, event)

        if (isEventFromHandle(event, handlesRefs) && event.target instanceof Element) {
            const handlePosition = getHandleCenterPosition(vertical, event.target)
            dragOffset.current = position - handlePosition
            position = handlePosition
        } else {
            dragOffset.current = 0
        }

        onStart(position)
    }

    /**
     * Mouse moving.
     */
    function onMouseMove(event: MouseEvent): void {
        if (!sliderRef.current) {
            onEnd()

            return
        }

        const position = getMousePosition(vertical, event)
        onMove(position - dragOffset.current)
    }

    /**
     * Touch moving.
     */
    function onTouchMove(event: TouchEvent): void {
        if (!sliderRef.current || isNotCorrectTouchEvent(event)) {
            onEnd()

            return
        }

        const position = getTouchPosition(vertical, event)
        onMove(position - dragOffset.current)
    }

    /**
     * Stop mouse/touch event.
     */
    function onEventEnd(): void {
        onEnd()
        onAfterChange(bounds)
    }

    /**
     * Add mouse events to the document.
     */
    function addDocumentMouseEvents(): void {
        document.addEventListener("mousemove", onMouseMove)
        document.addEventListener("mouseup", onEventEnd)
    }

    /**
     * Add touch events to the document.
     */
    function addDocumentTouchEvents(): void {
        document.addEventListener("touchmove", onTouchMove)
        document.addEventListener("touchend", onEventEnd)
    }

    /**
     * Remove mouse and touch events from the document.
     */
    function removeDocumentEvents(): void {
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("mouseup", onEventEnd)

        document.removeEventListener("touchmove", onTouchMove)
        document.removeEventListener("touchend", onEventEnd)
    }

    /**
     * Render base markup of the component.
     */
    return (
        <div
            class={classes}
            ref={sliderRef}
            onTouchStart={disabled ? noop : onTouchStart}
            onMouseDown={disabled ? noop : onMouseDown}
        >
            <div class={`${classesPrefix}rail`} />
            {tracks}
            <Steps
                vertical={vertical}
                marks={marks}
                dots={dots}
                step={step}
                included={included}
                lowerBound={lowerBound}
                upperBound={upperBound}
                max={max}
                min={min}
                classesPrefix={classesPrefix}
            />
            {handles}
            <Marks
                vertical={vertical}
                marks={marks}
                included={included}
                lowerBound={lowerBound}
                upperBound={upperBound}
                max={max}
                min={min}
                classesPrefix={classesPrefix}
            />
            {children}
        </div>
    )
}
